import React, { useState } from "react";
import { Card, Row, Col, Badge, Tooltip } from "antd";
import {
  UserOutlined,
  CrownOutlined,
  TeamOutlined,
  ToolOutlined,
  CheckCircleFilled,
  AppstoreOutlined,
} from "@ant-design/icons";
import { ePrivileges } from "../../utils/enum";
import "./role-template-selector.css";

export interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  privilegeIds: string[];
  privilegeCount: number;
}

const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: "client",
    name: "Client",
    description: "View-only access to MOM and projects",
    icon: <UserOutlined />,
    color: "var(--primary-shade-4)",
    privilegeCount: 10,
    privilegeIds: [
      // MOM privileges
      ePrivileges.VIEW_CLIENT,
      ePrivileges.VIEW_FOLDER,
      ePrivileges.VIEW_DRAFT,
      ePrivileges.PLAY_AUDIO,
      ePrivileges.VIEW_TRANSCRIPT,
      ePrivileges.VIEW_SUMMARY,
      // Project viewing
      ePrivileges.VIEWPROJECT,
      // Dashboard
      ePrivileges.VIEWDASHBOARD,
    ],
  },
  {
    id: "manager",
    name: "Manager",
    description: "Full project and team management",
    icon: <CrownOutlined />,
    color: "var(--primary-shade-4)",
    privilegeCount: 70,
    privilegeIds: [
      // Dashboard - All
      ePrivileges.VIEWDASHBOARD,
      ePrivileges.ADDTASKCATEGORY,
      ePrivileges.EDITTASKCATEGORY,
      ePrivileges.DELETEWORKCATEGORY,
      ePrivileges.ADDTASK,
      ePrivileges.EDITTASK,
      ePrivileges.DELETETASK,
      ePrivileges.ADDWORK,
      ePrivileges.EDITWORK,
      ePrivileges.DELETEWORK,
      ePrivileges.VIEWWORK,
      ePrivileges.VIEWTASK,
      ePrivileges.GENERATEREPORT,
      ePrivileges.VIEWTASKTIMETRACKING,
      ePrivileges.VIEWTASKHISTORY,
      // Project Management - All
      ePrivileges.VIEWPROJECT,
      ePrivileges.ADDPROJECT,
      ePrivileges.EDITPROJECT,
      ePrivileges.DELETEPROJECT,
      ePrivileges.VIEW_ALL_PROJECTS,
      ePrivileges.VIEW_PRIVATE_FOLDERS,
      // Category Configuration - All
      ePrivileges.VIEWPROJECTCATEGORY,
      ePrivileges.VIEWTASKCATEGORY,
      ePrivileges.VIEWSTATUS,
      ePrivileges.ADDPROJECTCATEGORY,
      ePrivileges.EDITPROJECTCATEGORY,
      ePrivileges.DELETEPROJECTCATEGORY,
      ePrivileges.ADDTASKCATEGORY,
      ePrivileges.EDITTASKCATEGORY,
      ePrivileges.DELETETASKCATEGORY,
      ePrivileges.ADDSTATUS,
      ePrivileges.EDITSTATUS,
      ePrivileges.DELETESTATUS,
      // Project Configuration - All
      ePrivileges.VIEWTASKCONFIGURATION,
      ePrivileges.ADDTASKCONFIGRUATION,
      ePrivileges.EDITTASKCONFIGURATION,
      ePrivileges.DELETETASKCONFIGURATION,
      ePrivileges.VIEWSTATUSCONFIGURATION,
      ePrivileges.ADDSTATUSCONFIGURATION,
      ePrivileges.EDITSTATUSCONFIGURATION,
      ePrivileges.DELETESTATUSCONFIGURATION,
      // Role Management - View only
      ePrivileges.VIEWROLE,
      // User Management
      ePrivileges.VIEWUSER,
      ePrivileges.ADDUSER,
      ePrivileges.EDITUSER,
      // MOM - All non-admin
      ePrivileges.VIEW_CLIENT,
      ePrivileges.ADD_CLIENT,
      ePrivileges.EDIT_CLIENT,
      ePrivileges.DELETE_CLIENT,
      ePrivileges.ADD_FOLDER,
      ePrivileges.VIEW_FOLDER,
      ePrivileges.EDIT_FOLDER,
      ePrivileges.DELETE_FOLDER,
      ePrivileges.UPLOAD_DOCUMENT,
      ePrivileges.MEETING_RECORDER,
      ePrivileges.GENERATE_BRD,
      ePrivileges.GENERATE_MOM,
      ePrivileges.VIEW_DRAFT,
      ePrivileges.DELETE_DRAFT,
      ePrivileges.EDIT_DRAFT,
      ePrivileges.MOVE_DRAFT,
      ePrivileges.PLAY_AUDIO,
      ePrivileges.VIEW_TRANSCRIPT,
      ePrivileges.VIEW_SUMMARY,
      ePrivileges.RENAME_DRAFT,
      ePrivileges.VIEW_ALL_CLIENTS,
    ],
  },
  {
    id: "employee",
    name: "Employee",
    description: "Task execution and viewing rights",
    icon: <TeamOutlined />,
    color: "var(--primary-shade-4)",
    privilegeCount: 18,
    privilegeIds: [
      // Dashboard - Task related
      ePrivileges.VIEWDASHBOARD,
      ePrivileges.ADDTASK,
      ePrivileges.EDITTASK,
      ePrivileges.VIEWASSIGNEDTASK,
      ePrivileges.VIEWWORK,
      ePrivileges.VIEWTASKTIMETRACKING,
      ePrivileges.VIEWTASKHISTORY,
      // Project viewing
      ePrivileges.VIEWPROJECT,
      // Category Configuration - View only
      ePrivileges.VIEWPROJECTCATEGORY,
      ePrivileges.VIEWTASKCATEGORY,
      ePrivileges.VIEWSTATUS,
      // MOM - View only
      ePrivileges.VIEW_FOLDER,
      ePrivileges.VIEW_DRAFT,
      ePrivileges.PLAY_AUDIO,
      ePrivileges.VIEW_TRANSCRIPT,
      ePrivileges.VIEW_SUMMARY,
    ],
  },
  {
    id: "custom",
    name: "Custom",
    description: "Manually select privileges",
    icon: <ToolOutlined />,
    color: "var(--primary-shade-4)",
    privilegeCount: 0,
    privilegeIds: [],
  },
];

interface RoleTemplateSelectorProps {
  onTemplateSelect: (template: RoleTemplate) => void;
  selectedTemplateId?: string;
}

const RoleTemplateSelector: React.FC<RoleTemplateSelectorProps> = ({
  onTemplateSelect,
  selectedTemplateId,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="role-template-container">
      <div className="template-header">
        <h3>
          <AppstoreOutlined style={{ marginRight: 8 }} />
          Quick Start Templates
        </h3>
        <p>Choose a predefined role template or start from scratch</p>
      </div>
      <Row gutter={[16, 16]}>
        {ROLE_TEMPLATES.map((template) => (
          <Col xs={24} sm={12} md={6} key={template.id}>
            <Tooltip title={template.description} placement="top">
              <Card
                className={`role-template-card ${
                  selectedTemplateId === template.id ? "selected" : ""
                } ${hoveredId === template.id ? "hovered" : ""}`}
                onClick={() => onTemplateSelect(template)}
                onMouseEnter={() => setHoveredId(template.id)}
                onMouseLeave={() => setHoveredId(null)}
                hoverable
              >
                {selectedTemplateId === template.id && (
                  <CheckCircleFilled className="selected-icon" />
                )}
                <div className="card-content">
                  <div className="icon-name-row">
                    <div className="icon-wrapper">
                      <div style={{ fontSize: "32px", color: template.color }}>
                        {template.icon}
                      </div>
                    </div>

                    <h4 className="role-name">{template.name}</h4>
                  </div>

                  {template.id !== "custom" && (
                    <Badge
                      count={`${template.privilegeCount} privileges`}
                      style={{
                        background:
                          "linear-gradient(135deg, #ffa1a1 0%, #ff5b5b 100%)",
                        color: "#fff",
                        fontSize: "11px",
                      }}
                      className="privilege-badge"
                    />
                  )}
                  <p className="role-description">{template.description}</p>
                </div>
              </Card>
            </Tooltip>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default RoleTemplateSelector;
export { ROLE_TEMPLATES };
